import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../Button';
import { Icon } from '../Icon';
import { handleError } from '../../helpers';

import './TestButton.css';

// No outcome yet: nothing to show beside the button and nothing under it.
const emptyResult = { status: null, msg: '', detail: '' };

/**
 * What to show under the button when the request failed rather than answered.
 *
 * A rejected request does not always carry the { message, error } an endpoint's own failure does:
 * one rejected by validation carries dataValidationError instead, and a request can fail carrying
 * neither. An empty string leaves the line off the page altogether, which is better than showing a
 * sentence made out of nothing.
 *
 * @param {object} err - The value the service call rejected with.
 * @returns {string}
 */
const errorDetail = err => {
  if (err?.dataValidationError) {
    return err.dataValidationError.map(error => error.msg).join('\n');
  }

  return [err?.message, err?.error].filter(Boolean).join(':\n ');
};

/**
 * A button that asks the backend to check something and reports the outcome in place: a headline
 * beside the button, and what the backend had to say under it. While the check is in flight the row
 * offers a Cancel link, so a check that hangs -- an unreachable host, a mail server that never
 * answers -- can be given up on.
 *
 * A test endpoint grades its own outcome: it answers with a `status` of 'success', 'warning' or
 * 'failed', a `message` that is the headline for that outcome, and a `detail` carrying whatever it
 * has to say beyond the verdict. This renders that as it comes, so a call site only hands over the
 * call.
 *
 * @param {object} props
 * @param {string} props.value - The button label.
 * @param {function} props.onClick - Runs the check. Receives the AbortController the Cancel link
 *   aborts -- not the click event -- to be passed on to the service call. Resolves with the
 *   endpoint's body, or undefined when the request was cancelled.
 * @param {string} [props.pendingMessage='Testing...'] - Shown beside the button while in flight.
 * @param {string} [props.failureMessage='Test failed'] - Headline for a request that failed
 *   outright, as opposed to one that answered with a failed status of its own.
 * @param {*} [props.resetKey] - The result describes the values as they were when the check ran, so
 *   it is cleared whenever this changes. Pass the form state the check reads.
 * @param {boolean} [props.disabled=false] - Greys the button out and ignores clicks.
 * @param {string} [props.tooltip] - Tooltip shown on hover.
 * @param {string} [props.className] - Additional class names for the row.
 */
export const TestButton = ({
  value,
  onClick,
  pendingMessage = 'Testing...',
  failureMessage = 'Test failed',
  resetKey,
  disabled = false,
  tooltip,
  className,
}) => {
  const [result, setResult] = useState(emptyResult);
  const [testing, setTesting] = useState(false);

  // Held in a ref rather than in state so that cancelling can swap in a fresh controller without a
  // re-render, and so the unmount cleanup below aborts the controller of the test actually running.
  const abortController = useRef(new AbortController());

  useEffect(() => {
    return () => abortController.current.abort();
  }, []);

  // Returning prev unchanged when there is nothing to clear keeps the mount pass, and every later
  // change that arrives with no result on screen, from causing a render.
  useEffect(() => {
    setResult(prev => (prev.status || prev.msg || prev.detail ? emptyResult : prev));
  }, [resetKey]);

  /**
   * Runs the check and reports what came back.
   * @param {MouseEvent} [e] - Absent when the button was activated from the keyboard.
   * @returns {Promise<void>}
   */
  const runTest = async e => {
    e?.preventDefault();

    setResult(emptyResult);
    setTesting(true);

    try {
      const res = await onClick(abortController.current);

      // A cancelled request resolves undefined all the way through customFetch and handleResponse,
      // so there is nothing to report and the row simply empties.
      if (res) {
        setResult({ status: res.status, msg: res.message, detail: res.detail });
      }
    } catch (err) {
      // handleError is called without showMessage: the outcome belongs beside the button that asked
      // for it, not in a toast. It is still what recognises an abort and says nothing.
      handleError({ err }, () => {
        setResult({ status: 'failed', msg: failureMessage, detail: errorDetail(err) });
      });
    } finally {
      setTesting(false);
    }
  };

  /** Gives up on the test in flight and arms a fresh controller for the next one. */
  const cancelTest = () => {
    abortController.current.abort();
    abortController.current = new AbortController();
  };

  return (
    <React.Fragment>
      <div className={'form-block horizontal' + (className ? ' ' + className : '')}>
        {/* Button runs its own loading state off the awaited onClick, so the shimmer and the
            re-entry guard come for free -- no testing flag and no loading class are passed here. */}
        <Button value={value} onClick={runTest} disabled={disabled} tooltip={tooltip} />

        <div className={'test-button-result' + (result.status ? ' ' + result.status : '')}>
          {testing ? (
            <div className="test-button-result-message">
              <div>{pendingMessage}</div>
              <div className="test-button-cancel" onClick={cancelTest}>
                Cancel
              </div>
            </div>
          ) : (
            <React.Fragment>
              {result.status && <Icon name={result.status} />}
              <div className="test-button-result-message">{result.msg}</div>
            </React.Fragment>
          )}
        </div>
      </div>

      {/* The outcome is announced beside the button, where there is room for a headline and no
          more. What the backend had to say is a sentence, so it goes under the button on a line of
          its own, outside that row. */}
      {result.detail && (
        <div className={'test-button-result-detail' + (result.status ? ' ' + result.status : '')}>
          {result.detail}
        </div>
      )}
    </React.Fragment>
  );
};
